import {
  ArrowDownward,
  ArrowUpward,
  FilterList,
  MoreVert
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import TruncatedCell from "./cells/TruncatedCell";
import { renderActiveFilterPopover } from "./filtering/renderActiveFilterPopover";
import { useColumnResize } from "./layout/useColumnResize";
import { useRowSelection } from "./selection/useRowSelection";


export default function DataTable({ 
  columns, 
  rows, 
  onDeleteRequest,
  title = "Data",
  emptyMessage = "Data Loading...",
  loading = false,
  error = null,
  searchTerm = "",
  renderCellContent,
  renderCell,
  filterPopoverByField = {},
  renderRowActionsMenu,
  renderRowActions,
  renderFilterPopover,
  renderFilterChips,
  // Server-side pagination props
  serverSidePagination = false,
  page: externalPage,
  rowsPerPage: externalRowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  hasNextPage = true,
  hasPrevPage = false,
  // New simplified pagination props
  onNextPage,
  onPreviousPage,
  // Filter callback for server-side filtering
  onFiltersChange,
  // Sort callback for server-side sorting
  onSortChange,
  sortConfig: externalSortConfig = null,
  // Row selection for bulk actions
  selectedRows: externalSelectedRows = [],
  onSelectedRowsChange,
  renderSelectionToolbar,
}) {
  const effectiveRenderCell = renderCell || renderCellContent;
  const effectiveRenderRowActions = renderRowActions || renderRowActionsMenu;
  const nonTruncatingColumnTypes = useMemo(
    () => new Set(["actions", "status", "notes", "accounts", "latestLogin"]),
    []
  );
  const effectiveFilterPopoverByField =
    typeof renderFilterPopover === "function"
      ? renderFilterPopover({ filterPopoverByField }) || filterPopoverByField
      : filterPopoverByField;

  // Use external pagination state if server-side, otherwise use local state
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(15);
  
  const page = serverSidePagination ? externalPage : internalPage;
  const rowsPerPage = serverSidePagination ? externalRowsPerPage : internalRowsPerPage;
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [internalSortConfig, setInternalSortConfig] = useState({ field: null, direction: null });
  const sortConfig = serverSidePagination && externalSortConfig ? externalSortConfig : internalSortConfig;
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilterField, setActiveFilterField] = useState(null);

  const activeFilterColumn = useMemo(
    () => columns.find((col) => col.field === activeFilterField),
    [columns, activeFilterField]
  );

  const hasCustomFilterPopover = useMemo(() => {
    if (!activeFilterField) return false;
    return typeof effectiveFilterPopoverByField?.[activeFilterField] === "function";
  }, [activeFilterField, effectiveFilterPopoverByField]);

  const isTextFilterPopover = useMemo(() => {
    if (!activeFilterField) return false;

    if (hasCustomFilterPopover) return false;

    return activeFilterColumn?.type !== "sort";
  }, [activeFilterColumn?.type, activeFilterField, hasCustomFilterPopover]);

  const shouldShowQuickSortMenu = useMemo(() => {
    if (!activeFilterField) return false;

    if (hasCustomFilterPopover) return false;

    return activeFilterColumn?.type !== "sort";
  }, [activeFilterColumn?.type, activeFilterField, hasCustomFilterPopover]);
  
  // Row actions menu state (generic; menu content is provided via render prop)
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  
  const filteredAndSortedRows = useMemo(() => {
    // Skip client-side filtering for server-side pagination
    // The server handles filtering, we just display the results
    let filtered = serverSidePagination
      ? rows
      : rows.filter((row) => {
          return Object.entries(filters).every(([field, filterValue]) => {
            if (!filterValue) return true;
            const cellValue = row[field]?.toString() || "";
            return cellValue.toLowerCase().startsWith(filterValue.toLowerCase());
          });
        });

    // Skip client-side sorting for server-side pagination
    if (!serverSidePagination && sortConfig.field && sortConfig.direction) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.field]?.toString() || "";
        const bVal = b[sortConfig.field]?.toString() || "";
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      });
    }

    return filtered;
  }, [rows, filters, sortConfig, serverSidePagination]);

  const paginatedRows = serverSidePagination
    ? filteredAndSortedRows
    : filteredAndSortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const filteredRowIds = useMemo(
    () =>
      filteredAndSortedRows
        .map((row) => row.id)
        .filter((id) => id !== undefined && id !== null),
    [filteredAndSortedRows]
  );

  const {
    selectedRows,
    setSelectedRows,
    isAllSelected,
    isSomeSelected,
    handleSelectAll,
    handleSelectRow,
    isRowSelected,
  } = useRowSelection({
    externalSelectedRows,
    onSelectedRowsChange,
    filteredRowIds,
    clearSelectionDeps: [filters, page],
  });

  const { tableRef, columnWidths, handleMouseDown } = useColumnResize();

  // Reset pagination when search term changes (only for client-side)
  useEffect(() => {
    if (!serverSidePagination) {
      setInternalPage(0);
    }
  }, [searchTerm, serverSidePagination]);

  const handleChangePage = (event, newPage) => {
    if (serverSidePagination) {
      // Use simplified pagination if available, otherwise fall back to old approach
      if (onNextPage && onPreviousPage) {
        const currentPage = externalPage !== undefined ? externalPage : 0;
        if (newPage > currentPage) {
          onNextPage();
        } else {
          onPreviousPage();
        }
      } else if (onPageChange) {
        onPageChange(event, newPage);
      }
    } else {
      setInternalPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    if (serverSidePagination && onRowsPerPageChange) {
      onRowsPerPageChange(event);
    } else {
      setInternalRowsPerPage(parseInt(event.target.value, 10));
      setInternalPage(0);
    }
  };

  const handleFilterChange = useCallback(
    (field, value, applyImmediately = false) => {
      const updateFilterObject = (source, fieldName, fieldValue) => {
        const next = { ...source };
        if (fieldValue && String(fieldValue).length > 0) {
          next[fieldName] = fieldValue;
        } else {
          delete next[fieldName];
        }
        return next;
      };

      setFilters((prev) => updateFilterObject(prev, field, value));

      const shouldApplyNow = !serverSidePagination || applyImmediately;
      if (!shouldApplyNow) {
        if (!serverSidePagination) {
          setInternalPage(0);
        }
        return;
      }

      const updatedApplied = updateFilterObject(appliedFilters, field, value);
      setAppliedFilters(updatedApplied);

      if (!serverSidePagination) {
        setInternalPage(0);
      }

      if (serverSidePagination && onFiltersChange) {
        setSelectedRows([]);
        onFiltersChange(updatedApplied);
      }
    },
    [appliedFilters, serverSidePagination, onFiltersChange, setSelectedRows]
  );

  const showFilterChipSection = typeof renderFilterChips === "function";

  const handleSort = useCallback((field, direction) => {
    if (serverSidePagination && onSortChange) {
      // For server-side sorting, call the parent handler
      onSortChange(field, direction);
    } else {
      // For client-side sorting, update local state
      setInternalSortConfig({ field, direction });
    }
  }, [onSortChange, serverSidePagination]);

  const handleFilterMenuOpen = (event, field) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterField(field);
  };

  const handleFilterMenuClose = useCallback(() => {
    setFilterAnchorEl(null);
    setActiveFilterField(null);
  }, []);

  const handleActionsMenuOpen = (event, row) => {
    event.stopPropagation();
    setActionsAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleActionsMenuClose = () => {
    setActionsAnchorEl(null);
    setSelectedRow(null);
  };

  // Domain navigation/menu handlers are owned by the parent.

  // Delete requests are handled by the extracted menus.

  // Domain-specific row filtering should be implemented by the caller.

  const resolveCellContent = useCallback(
    (cellValue, column, row) => {
      if (typeof effectiveRenderCell === "function") {
        const rendered = effectiveRenderCell(cellValue, column, row);
        if (rendered !== undefined) return rendered;
      }

      if (cellValue === "-" || cellValue === null || cellValue === undefined) {
        return "";
      }

      return cellValue;
    },
    [effectiveRenderCell]
  );

  const filterPopoverContext = useMemo(
    () => ({
      activeFilterField,
      activeFilterColumn,
      filters,
      setFilters,
      sortConfig,
      handleSort,
      handleFilterChange,
      handleFilterMenuClose,
      rows,
      columns,
    }),
    [
      activeFilterColumn,
      activeFilterField,
      columns,
      filters,
      handleFilterChange,
      handleFilterMenuClose,
      handleSort,
      rows,
      sortConfig,
    ]
  );

  // Show loading state if loading, regardless of rows length
  if (loading) {
    return (
      <Paper sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress size={40} sx={{ color: "#627CFB" }} />
          <Typography variant="body2" color="text.secondary">
            Loading data...
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Only show early return empty state if there are no rows AND no filters applied
  const hasActiveFilters = Object.values(filters).some((f) => f) || sortConfig.field;
  if (rows.length === 0 && !hasActiveFilters) {
    return (
      <Paper sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
        <Typography variant="h6" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Paper>
    );
  }

  // Filter popovers are injected via `filterPopoverByField`.

  return (
    <Paper sx={{ 
      borderRadius: "12px", 
      overflow: "hidden", 
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid rgba(229, 231, 235, 0.8)"
    }}>
      {(showFilterChipSection || (selectedRows.length > 0 && typeof renderSelectionToolbar === "function")) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            gap: 1.5,
            px: 3,
            py: 2,
            borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
            backgroundColor: '#F9FAFB',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 1.5,
              flexWrap: 'wrap',
              alignItems: { xs: 'flex-start', lg: 'center' },
              flexGrow: 1,
            }}
          >
            {typeof renderFilterChips === "function"
              ? renderFilterChips({
                  appliedFilters,
                  filters,
                  handleFilterChange,
                  clearAllFilters: () => {
                    setFilters({});
                    setAppliedFilters({});
                    if (!serverSidePagination) {
                      setInternalPage(0);
                    }
                    if (serverSidePagination && onFiltersChange) {
                      setSelectedRows([]);
                      onFiltersChange({});
                    }
                  },
                })
              : null}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                width: { xs: '100%', md: 'auto' },
                ml: { xs: 0, md: 'auto' },
                minHeight: 40,
                flexShrink: 0
              }}
            >
              {selectedRows.length > 0 && typeof renderSelectionToolbar === "function"
                ? renderSelectionToolbar({
                    selectedRows,
                    clearSelection: () => setSelectedRows([]),
                  })
                : null}
            </Box>
        </Box>
      </Box>
    )}
      <TableContainer 
        ref={tableRef}
        sx={{
          maxHeight: 'calc(100vh - 280px)',
          overflowY: 'auto',
          overflowX: 'auto'
        }}
      >
        <Table sx={{ tableLayout: "auto" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#EEF0FD" }}>
              <TableCell sx={{ 
                width: "60px", 
                padding: "16px 8px",
                position: 'sticky',
                top: 0,
                backgroundColor: "#EEF0FD",
                zIndex: 10
              }}>
                <Checkbox 
                  size="small"
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              {columns.map((column) => (
                <TableCell 
                  key={column.field}
                  sx={{ 
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "#374151",
                    padding: "16px 8px",
                    borderBottom: "1px solid #e5e7eb",
                    width: columnWidths[column.field] || 'auto',
                    minWidth: columnWidths[column.field] || 150,
                    maxWidth: columnWidths[column.field] || 'none',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: "#EEF0FD",
                    zIndex: 10
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {column.headerName}
                    </Typography>
                    {/* Show sort indicator if this column is currently sorted */}
                    {sortConfig.field === column.field && (
                      <Box sx={{ ml: 0.5 }}>
                        {sortConfig.direction === 'asc' ? (
                          <ArrowUpward fontSize="small" sx={{ color: '#627CFB' }} />
                        ) : (
                          <ArrowDownward fontSize="small" sx={{ color: '#627CFB' }} />
                        )}
                      </Box>
                    )}
                    {!column.disableFilter && (
                      <IconButton
                        size="small"
                        aria-label={`Open filter for ${column.headerName}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFilterMenuOpen(e, column.field);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <FilterList fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {/* Resize handle */}
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, column.field)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '5px',
                      cursor: 'col-resize',
                      userSelect: 'none',
                      borderRight: '1px solid rgba(209, 213, 219, 0.6)', // Subtle default border
                      '&:hover': {
                        borderRight: '2px solid #627CFB'
                      },
                      '&:active': {
                        borderRight: '2px solid #627CFB'
                      }
                    }}
                  />
                </TableCell>
              ))}
              {/* Actions column header - only show if no custom actions column exists */}
              {!columns.some((col) => String(col.type || "").toLowerCase().includes("actions")) && (
                <TableCell 
                  sx={{ 
                    padding: "16px 8px",
                    borderBottom: "1px solid #e5e7eb",
                    width: "60px"
                  }}
                />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} sx={{ textAlign: "center", py: 4 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <CircularProgress size={40} sx={{ color: "#627CFB" }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading data...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} sx={{ textAlign: "center", py: 4 }}>
                  <Alert severity="error" sx={{ maxWidth: 400, mx: "auto" }}>
                    {error}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {hasActiveFilters ? "No results found" : emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, index) => (
                <TableRow 
                  key={row.id || index}
                  hover
                  sx={{ 
                    "&:hover": { backgroundColor: "#f9fafb" },
                    borderBottom: "1px solid #f3f4f6",
                    backgroundColor: isRowSelected(row.id) ? "#f0f9ff" : "inherit"
                  }}
                >
                  <TableCell sx={{ padding: "16px 8px" }}>
                    <Checkbox 
                      size="small"
                      checked={isRowSelected(row.id)}
                      onChange={(e) => handleSelectRow(e, row.id)}
                    />
                  </TableCell>
                  {columns.map((column) => {
                    const cellValue = row[column.field];
                    const cellContent = resolveCellContent(cellValue, column, row);
                    const shouldWrapWithTruncation =
                      !nonTruncatingColumnTypes.has(column.type);

                    let displayText = "";
                    if (typeof cellValue === "string") {
                      displayText = cellValue;
                    } else if (typeof cellValue === "number") {
                      displayText = cellValue.toString();
                    }

                    return (
                      <TableCell
                        key={column.field}
                        sx={{
                          padding: "16px 8px",
                          fontSize: "0.875rem",
                          color: "#111827",
                          maxWidth: columnWidths[column.field] || 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {shouldWrapWithTruncation ? (
                          <TruncatedCell displayText={displayText}>{cellContent}</TruncatedCell>
                        ) : (
                          cellContent
                        )}
                      </TableCell>
                    );
                  })}
                  {/* Actions column - only show if no custom actions column exists */}
                  {!columns.some((col) => String(col.type || "").toLowerCase().includes("actions")) && (
                    <TableCell sx={{ padding: "16px 8px" }}>
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleActionsMenuOpen(e, row)}
                        sx={{ color: "#6b7280" }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Popover
        open={Boolean(filterAnchorEl && activeFilterField)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          zIndex: 9999,
          '& .MuiPopover-paper': {
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(229, 231, 235, 0.8)',
            minWidth: '200px'
          }
        }}
      >
        {renderActiveFilterPopover({
          activeFilterField,
          activeFilterColumn,
          filterPopoverByField: effectiveFilterPopoverByField,
          filterPopoverContext,
          isTextFilterPopover,
          shouldShowQuickSortMenu,
          filters,
          setFilters,
          sortConfig,
          handleSort,
          handleFilterChange,
          handleFilterMenuClose,
        })}
      </Popover>

      {typeof effectiveRenderRowActions === "function"
        ? effectiveRenderRowActions({
            anchorEl: actionsAnchorEl,
            onClose: handleActionsMenuClose,
            row: selectedRow,
            onDeleteRequest,
          })
        : null}

      <TablePagination
        component="div"
        count={serverSidePagination 
          ? (totalCount !== undefined && totalCount >= 0 ? totalCount : -1) // Use -1 for unknown count
          : filteredAndSortedRows.length
        }
        page={serverSidePagination ? (externalPage !== undefined ? externalPage : 0) : internalPage}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[15, 30, 45, 60]}
        labelDisplayedRows={
          serverSidePagination
            ? ({ from, to }) => `${from}–${to}`
            : undefined
        }
        nextIconButtonProps={{
          disabled: serverSidePagination && !hasNextPage
        }}
        backIconButtonProps={{
          disabled: serverSidePagination && !hasPrevPage
        }}
        SelectProps={{
          MenuProps: {
            sx: { zIndex: 99999 }
          }
        }}
        sx={{
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
          "& .MuiTablePagination-toolbar": {
            paddingLeft: "16px",
            paddingRight: "16px"
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: "0.875rem",
            color: "#6b7280"
          }
        }}
      />

    </Paper>
  )}
